import axios from "../../api/axiosConfig";
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

var lastNotificationID = "";

function arrayBufferToBase64(buffer) {
    var binary = '';
    var bytes = new Uint8Array(buffer);
    var len = bytes.byteLength;
    for (var i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}

function sendWebSubscriptionToBackend(subscription) {
    axios.post(`${import.meta.env.VITE_PUBLIC_DOMAIN}notification/webSubscribe`, {
        endpoint: subscription.endpoint,
        p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
        auth: arrayBufferToBase64(subscription.getKey('auth'))
    }).then(response => {
        if (response.status === 200) {

            console.log('Web Subscription sent to backend successfully.');
        } else {
            throw new Error('Failed to send subscription to backend');
        }
    }).catch(error => {
        console.error('Error sending subscription to backend:', error);
    });
}

function sendMobileTokenToBackend(token) {

    const capPlatform = Capacitor.getPlatform();
    let platform = "iOS";

    if (capPlatform.toLowerCase() === 'android') {
        platform = "Android";
    }

    axios.post(`${import.meta.env.VITE_PUBLIC_DOMAIN}notification/mobileSubscribe`, {
        token: token,
        Platform: platform
    }).then(response => {
        if (response.status === 200) {

            console.log('Mobile Subscription sent to backend successfully.');
        } else {
            throw new Error('Failed to send mobile subscription to backend');
        }
    }).catch(error => {
        console.error('Error sending mobile subscription to backend:', error);
    });
}

export async function getSubscription() {
    if (!('serviceWorker' in navigator)) {
        console.log('Service workers are not supported in this browser.');
        return;
    }

    try {
        const registration = await navigator.serviceWorker.ready;
        let subscription = await registration.pushManager.getSubscription();
        const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;

        if (subscription) {

            const response = await axios.post(`${import.meta.env.VITE_PUBLIC_DOMAIN}notification/getSubscription`, subscription);

            if (response.status === 200) {
                const existingSubscriptions = response.data;

                if (!Capacitor.isNativePlatform()) {

                    const webSubscriptions = Object.keys(existingSubscriptions.webSubscriptions).length === 0;

                    if (webSubscriptions) {
                        console.log("No existing subscription found in backend, subscribing user...");
                        subscription = await registration.pushManager.subscribe({
                            userVisibleOnly: true,
                            applicationServerKey: vapidPublicKey
                        });
                        await sendWebSubscriptionToBackend(subscription);
                    } else {
                        // Check if the subscription already exists in the backend
                        const existingSubscription = existingSubscriptions.webSubscriptions.find(s => s.endpoint === subscription.endpoint);
                        if (!existingSubscription) {
                            console.log("No matching subscription found, subscribing user again...");
                            subscription = await registration.pushManager.subscribe({
                                userVisibleOnly: true,
                                applicationServerKey: vapidPublicKey
                            });
                            await sendWebSubscriptionToBackend(subscription);
                        } else {
                            console.log("Subscription already exists in backend.");
                        }
                    }
                }
            }
            else {
                console.error("Failed to get subscription status from backend.");
            }

        } else {
            // If no subscription exists, create a new one
            console.log("No subscription found, subscribing user...");
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: vapidPublicKey
            });
            await sendWebSubscriptionToBackend(subscription);
        }

    } catch (error) {
        console.error('Error obtaining or sending subscription:', error);
    }
}

export const askForNotificationPermission = async () => {
    if (Capacitor.isNativePlatform()) {
        await registerCapacitorNotifications();
    } else {
        if ('Notification' in window) {
            if (Notification.permission === 'default') {
                await Notification.requestPermission().then((permission) => {
                    if (permission === 'granted') {
                        console.log('User granted notifications');
                    } else {
                        console.log('User denied notifications');
                    }
                });
            }
        }
        else {
            console.log("Notification API not supported by the browser");
        }
    }
};

const checkPermissions = async () => {
    const result = await FirebaseMessaging.checkPermissions();
    return result.receive;
};

const requestPermissions = async () => {
    const result = await FirebaseMessaging.requestPermissions();
    return result.receive;
};

const getToken = async () => {
    const result = await FirebaseMessaging.getToken();
    return result.token;
};

const registerCapacitorNotifications = async () => {
    let permissonStatus = await checkPermissions();
    if (permissonStatus === 'prompt') {
        permissonStatus = await requestPermissions();
    }
    if (permissonStatus === 'granted') {
        console.log('User granted notifications');

        await FirebaseMessaging.removeAllListeners();

        //add listeners
        await FirebaseMessaging.addListener('tokenReceived', event => {
            console.log('FCM token received');
        });

        await FirebaseMessaging.addListener('notificationReceived', event => {
            console.log('Push notification received');
            if (Capacitor.getPlatform() === 'android') {
                const notification = event.notification;
                //we receive multiple calls to notificationReceived and don't want to create multiple LocalNotifications,
                //so we check the notification ID here and only send if it doesn't match the last ID we received
                if (notification.id !== lastNotificationID) {
                    lastNotificationID = notification.id;
                    let message = "";
                    if (notification.body !== undefined) {
                        message = notification.body;
                    }
                    LocalNotifications.schedule({
                        notifications: [
                            {
                                title: notification.title,
                                body: message,
                                id: Math.ceil(Math.random() * 100),
                                ongoing: false,
                                extra: notification.data.taskId
                            }
                        ]
                    });
                }
            }
        });

        await FirebaseMessaging.addListener('notificationActionPerformed', event => {
            console.log('Notification action performed');
            const notification = event.notification;
            if (notification.data.taskId !== undefined) {
                const taskID = notification.data.taskId;
                window.location.href = `${import.meta.env.VITE_DEVICE_DOMAIN}Overview/${taskID}`;
            }
        });

        if (Capacitor.getPlatform() === 'android') {
            await LocalNotifications.removeAllListeners();
            
            await LocalNotifications.addListener('localNotificationReceived', event => {
                console.log('Local notification received');
            });

            await LocalNotifications.addListener('localNotificationActionPerformed', event => {
                console.log('Local notification action performed');
                const notification = event.notification;
                if (notification.extra !== undefined) {
                    const taskID = notification.extra;
                    window.location.href = `${import.meta.env.VITE_DEVICE_DOMAIN}Overview/${taskID}`;
                }
            });
        }

        const token = await getToken();
        sendMobileTokenToBackend(token);
    } else {
        console.log('User denied notifications');
        throw new Error('User denied permissions!');
    }
};